import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { FiSave, FiRefreshCw, FiToggleLeft, FiToggleRight, FiPlus, FiTrash2 } from 'react-icons/fi';
import { API_URL } from '../../Api/api';
import { getAuthToken } from '../../Api/authToken';
import fallbackDetails from '../../json/propertydetail';
import fallbackLandtypes from '../../json/landtype';
import {
  buildKnownCategoryIdSet,
  getRawShowList,
  getRawLandTypeShowList,
  isDetailFieldVisible,
  isLandTypeDropdownVisible,
  isOptInDetailField,
  normalizeShowIds,
  resolveCategoryEntityId,
  toggleCategoryInShowList,
} from '../../utils/detailFieldVisibility';
import {
  findInputByField,
  getInputFieldKeyForDetail,
  normalizeInputsList,
} from '../../utils/landAreaConfig';
import './AdminPages.css';
import './DetailConfigPage.css';

const authHeaders = () => ({ Authorization: `Bearer ${getAuthToken()}` });

const LAND_AREA_FIELDS = new Set([
  'plot_area',
  'buildup_area',
  'carpet_area',
  'land_area',
  'land_area1',
  'plot_size',
]);

/** Explicitly excluded — not a valid detail/config field */
const EXCLUDED_LAND_FIELDS = new Set(['land_area_cent']);

const isLandAreaField = (field) => {
  if (!field) return false;
  if (EXCLUDED_LAND_FIELDS.has(field.field)) return false;
  if (field.group === 'land_area') return true;
  if (LAND_AREA_FIELDS.has(field.field)) return true;
  if (field.type === 'area' || field.type === 'computed') return true;
  return false;
};

const unwrap = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
};

const humanizeKey = (key) => {
  if (!key) return '';
  return String(key)
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();
};

const resolveTypeId = (item) => {
  if (!item || typeof item !== 'object') return null;
  const n = Number(item.id ?? item.type_id);
  return Number.isFinite(n) ? n : null;
};

const resolveFieldLabel = (field) => {
  const label = field?.label ?? field?.Label ?? field?.name;
  if (label != null && String(label).trim()) return String(label).trim();
  const key = field?.field ?? field?.key;
  if (key) return humanizeKey(key);
  if (field?.id != null) return `Field ${field.id}`;
  return 'Untitled field';
};

const DEFAULT_CALC = {
  to_sqft: 1,
  to_sqmt: 1,
  to_sqyd: 1,
  to_cent: 1,
  to_acre: 1,
  to_ground: 1,
};

const enrichFields = (apiFields) => {
  const byId = new Map();
  const byKey = new Map();
  (Array.isArray(fallbackDetails) ? fallbackDetails : []).forEach((f) => {
    if (f?.id != null) byId.set(Number(f.id), f);
    if (f?.field) byKey.set(String(f.field), f);
  });

  const apiList = Array.isArray(apiFields) ? apiFields : [];
  const fallbackList = Array.isArray(fallbackDetails) ? fallbackDetails : [];

  const mergedMap = new Map();
  fallbackList.forEach((f) => {
    if (EXCLUDED_LAND_FIELDS.has(f?.field)) return;
    const key = f?.field ? String(f.field) : (f?.id != null ? `id:${f.id}` : null);
    if (key) mergedMap.set(key, { ...f });
  });
  apiList.forEach((f) => {
    if (EXCLUDED_LAND_FIELDS.has(f?.field)) return;
    const key = f?.field ? String(f.field) : (f?.id != null ? `id:${f.id}` : null);
    if (!key) return;
    mergedMap.set(key, { ...(mergedMap.get(key) || {}), ...f });
  });

  return [...mergedMap.values()]
    .filter((f) => !EXCLUDED_LAND_FIELDS.has(f?.field))
    .map((f, index) => {
      const fb = (f?.id != null && byId.get(Number(f.id)))
        || (f?.field && byKey.get(String(f.field)))
        || {};
      const merged = { ...fb, ...f };
      const detailShow = Array.isArray(f?.detail_show)
        ? f.detail_show
        : (Array.isArray(merged.detail_show) ? merged.detail_show : []);
      const projectShow = Array.isArray(f?.project_detail_show)
        ? f.project_detail_show
        : (Array.isArray(merged.project_detail_show) ? merged.project_detail_show : []);
      const cardLand = Array.isArray(f?.card_land_show)
        ? f.card_land_show
        : (Array.isArray(merged.card_land_show) ? merged.card_land_show : []);
      const landTypeShow = Array.isArray(f?.land_type_show)
        ? f.land_type_show
        : (Array.isArray(merged.land_type_show) ? merged.land_type_show : []);
      const projectLandTypeShow = Array.isArray(f?.project_land_type_show)
        ? f.project_land_type_show
        : (Array.isArray(merged.project_land_type_show) ? merged.project_land_type_show : []);

      return {
        ...merged,
        id: merged.id ?? fb.id ?? index + 1,
        field: merged.field || fb.field || '',
        label: resolveFieldLabel(merged),
        type: merged.type || fb.type || '',
        unit: merged.unit || fb.unit || '',
        group: merged.group || fb.group || (isLandAreaField(merged) ? 'land_area' : 'property'),
        detail_show: detailShow,
        project_detail_show: projectShow,
        card_show: Array.isArray(merged.card_show) ? merged.card_show : [],
        card_land_show: cardLand,
        land_type_show: landTypeShow,
        project_land_type_show: projectLandTypeShow,
      };
    })
    .sort((a, b) => Number(a.id || 0) - Number(b.id || 0));
};

const ToggleSwitch = ({ on, label }) => (
  <span className={`detail-config-toggle${on ? ' on' : ''}`}>
    {on ? <FiToggleRight size={28} /> : <FiToggleLeft size={28} />}
    <span>{label || (on ? 'Enabled' : 'Disabled')}</span>
  </span>
);

const DetailConfigPage = ({ mode = 'property' }) => {
  const isProject = mode === 'project';
  const showKey = isProject ? 'project_detail_show' : 'detail_show';
  const inputsEndpoint = isProject ? '/project-inputs' : '/property-inputs';
  const title = isProject ? 'Project Detail Config' : 'Property Detail Config';
  const subtitle = isProject
    ? 'Configure Land Area Details, Land Types, and other fields for Project Detail pages.'
    : 'Configure Land Area Details, Land Types, and other fields for Property Detail pages.';

  const [types, setTypes] = useState([]);
  const [categories, setCategories] = useState([]);
  const [fields, setFields] = useState([]);
  const [landtypes, setLandtypes] = useState([]);
  const [formInputs, setFormInputs] = useState([]);
  const [fromApi, setFromApi] = useState(false);
  const [typeId, setTypeId] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // dirty[fieldId] = { detail?: number[], cardLand?: number[], landType?: number[] }
  const [dirty, setDirty] = useState({});
  // dirtyLandtypeIds[inputFieldKey] = number[]
  const [dirtyLandtypeIds, setDirtyLandtypeIds] = useState({});
  // dirtyLandtypeNames[landtypeId] = string
  const [dirtyLandtypeNames, setDirtyLandtypeNames] = useState({});
  const [newLandtypeName, setNewLandtypeName] = useState('');

  const knownCategoryIds = useMemo(
    () => buildKnownCategoryIdSet(categories),
    [categories]
  );

  const allCategoryIds = useMemo(
    () => [...knownCategoryIds],
    [knownCategoryIds]
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const requests = [
        axios.get(`${API_URL}/category`, { headers: authHeaders() }),
        axios.get(`${API_URL}/property-detail`, { headers: authHeaders() }),
        axios.get(`${API_URL}/landtype`, { headers: authHeaders() }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}${inputsEndpoint}`, { headers: authHeaders() }).catch(() => ({ data: [] })),
      ];
      if (!isProject) {
        requests.unshift(axios.get(`${API_URL}/types/property`, { headers: authHeaders() }));
      }

      const results = await Promise.all(requests);
      const [typesRes, catsRes, fieldsRes, ltRes, inputsRes] = isProject
        ? [null, ...results]
        : results;

      const typeList = isProject ? [] : unwrap(typesRes?.data);
      const catList = unwrap(catsRes.data);
      const apiFields = unwrap(fieldsRes.data);
      const fieldList = enrichFields(apiFields);
      const ltList = unwrap(ltRes.data);
      const inputList = normalizeInputsList(unwrap(inputsRes.data));

      setTypes(typeList);
      setCategories(catList);
      setFields(fieldList);
      setLandtypes(ltList.length ? ltList : fallbackLandtypes);
      setFormInputs(inputList);
      setFromApi(apiFields.length > 0);
      setDirty({});
      setDirtyLandtypeIds({});
      setDirtyLandtypeNames({});

      if (isProject) {
        setTypeId('');
      } else {
        setTypeId((prev) => {
          if (prev && typeList.some((t) => String(resolveTypeId(t)) === String(prev))) return prev;
          const first = resolveTypeId(typeList[0]);
          return first != null ? String(first) : '';
        });
      }
    } catch (e) {
      toast.error(e?.response?.data?.error || e.message || 'Failed to load config');
      setFields(enrichFields([]));
      setLandtypes(fallbackLandtypes);
      setFromApi(false);
    } finally {
      setLoading(false);
    }
  }, [isProject, inputsEndpoint]);

  useEffect(() => { load(); }, [load]);

  const filteredCategories = useMemo(() => {
    if (isProject) {
      // Project Detail Config uses Category only (no Property Type filter).
      return categories.filter((c) => c.is_project === true);
    }
    const tid = Number(typeId);
    return categories.filter((c) => {
      const ids = Array.isArray(c.type_id)
        ? c.type_id.map(Number)
        : [Number(c.type_id)].filter((n) => !Number.isNaN(n));
      return !tid || ids.includes(tid);
    });
  }, [categories, typeId, isProject]);

  useEffect(() => {
    if (!filteredCategories.length) {
      setCategoryId('');
      return;
    }
    const stillValid = filteredCategories.some(
      (c) => String(resolveCategoryEntityId(c)) === String(categoryId)
    );
    if (!stillValid) {
      const first = resolveCategoryEntityId(filteredCategories[0]);
      setCategoryId(first != null ? String(first) : '');
    }
  }, [filteredCategories, categoryId]);

  const landAreaFields = useMemo(
    () => fields.filter(isLandAreaField),
    [fields]
  );
  const otherFields = useMemo(
    () => fields.filter((f) => !isLandAreaField(f)),
    [fields]
  );

  const resolveLinkedInput = (detailField) => {
    const candidates = [
      getInputFieldKeyForDetail(detailField.field, isProject),
      detailField.field,
      detailField.field === 'land_area' ? 'land_area1' : null,
    ].filter(Boolean);
    for (const key of candidates) {
      const found = findInputByField(formInputs, key);
      if (found && found.landtype) return found;
    }
    // Prefer any landtype-enabled input matching the label/field loosely
    for (const key of candidates) {
      const found = findInputByField(formInputs, key);
      if (found) return found;
    }
    return null;
  };

  const getEffectiveLandtypeIds = (detailField) => {
    const input = resolveLinkedInput(detailField);
    const inputKey = input?.field;
    if (inputKey && dirtyLandtypeIds[inputKey] !== undefined) {
      return dirtyLandtypeIds[inputKey];
    }
    if (input && Array.isArray(input.landtype_ids)) return input.landtype_ids.map(Number);
    return [];
  };

  const toggleLandtypeForField = (detailField, landtypeId) => {
    const input = resolveLinkedInput(detailField);
    if (!input?.field) {
      toast.info('No matching form land-area input for this field');
      return;
    }
    const ltId = Number(landtypeId);
    const current = getEffectiveLandtypeIds(detailField).map(Number);
    const next = current.includes(ltId)
      ? current.filter((id) => id !== ltId)
      : [...current, ltId];
    setDirtyLandtypeIds((prev) => ({ ...prev, [input.field]: next }));
  };

  const getLandtypeDisplayName = (lt) => {
    if (dirtyLandtypeNames[lt.id] !== undefined) return dirtyLandtypeNames[lt.id];
    return lt.landtype_name || '';
  };

  const getEffectiveDetailIds = (field) => {
    const d = dirty[field.id];
    if (d && d.detail !== undefined) return d.detail;
    return getRawShowList(field, { isProject });
  };

  const getEffectiveCardLandIds = (field) => {
    const d = dirty[field.id];
    if (d && d.cardLand !== undefined) return d.cardLand;
    return Array.isArray(field.card_land_show) ? field.card_land_show : [];
  };

  const isDetailEnabled = (field) => {
    if (!categoryId) return false;
    const d = dirty[field.id];
    if (d && d.detail !== undefined) {
      const ids = d.detail;
      if (!Array.isArray(ids) || ids.length === 0) return !isOptInDetailField(field);
      return ids.map(Number).includes(Number(categoryId));
    }
    return isDetailFieldVisible(field, categoryId, {
      isProject,
      knownCategoryIds,
    });
  };

  const isCardLandEnabled = (field) => {
    if (!categoryId) return false;
    const ids = getEffectiveCardLandIds(field);
    const normalized = normalizeShowIds(ids, knownCategoryIds);
    if (normalized == null) return false;
    return normalized.includes(Number(categoryId));
  };

  const getEffectiveLandTypeIds = (field) => {
    const d = dirty[field.id];
    if (d && d.landType !== undefined) return d.landType;
    return getRawLandTypeShowList(field, { isProject });
  };

  const isLandTypeEnabled = (field) => {
    if (!categoryId) return false;
    const d = dirty[field.id];
    if (d && d.landType !== undefined) {
      const ids = d.landType;
      if (!Array.isArray(ids) || ids.length === 0) return true;
      return ids.map(Number).includes(Number(categoryId));
    }
    return isLandTypeDropdownVisible(field, categoryId, {
      isProject,
      knownCategoryIds,
    });
  };

  const toggleDetail = (field) => {
    if (!categoryId || field?.id == null) return;
    const currentlyOn = isDetailEnabled(field);
    const current = getEffectiveDetailIds(field);
    const next = toggleCategoryInShowList(
      current,
      categoryId,
      allCategoryIds,
      currentlyOn,
      { optIn: isOptInDetailField(field) }
    );
    setDirty((prev) => ({
      ...prev,
      [field.id]: { ...prev[field.id], detail: next },
    }));
  };

  const toggleCardLand = (field) => {
    if (!categoryId || field?.id == null) return;
    const cat = Number(categoryId);
    const currentlyOn = isCardLandEnabled(field);
    const current = (getEffectiveCardLandIds(field) || []).map(Number).filter(Number.isFinite);
    const next = currentlyOn
      ? current.filter((id) => id !== cat)
      : [...new Set([...current, cat])];
    setDirty((prev) => ({
      ...prev,
      [field.id]: { ...prev[field.id], cardLand: next },
    }));
  };

  const toggleLandType = (field) => {
    if (!categoryId || field?.id == null) return;
    const currentlyOn = isLandTypeEnabled(field);
    const current = getEffectiveLandTypeIds(field);
    const next = toggleCategoryInShowList(
      current,
      categoryId,
      allCategoryIds,
      currentlyOn
    );
    setDirty((prev) => ({
      ...prev,
      [field.id]: { ...prev[field.id], landType: next },
    }));
  };

  const buildPayloadForField = (field) => {
    const d = dirty[field.id] || {};
    const detailIds = d.detail !== undefined
      ? d.detail
      : (getRawShowList(field, { isProject }) || []);
    const cardLandIds = d.cardLand !== undefined
      ? d.cardLand
      : (Array.isArray(field.card_land_show) ? field.card_land_show : []);
    const landTypeIds = d.landType !== undefined
      ? d.landType
      : (getRawLandTypeShowList(field, { isProject }) || []);

    return {
      id: field.id,
      label: field.label,
      field: field.field,
      unit: field.unit || '',
      type: field.type || 'text',
      group: field.group || (isLandAreaField(field) ? 'land_area' : 'property'),
      card_show: Array.isArray(field.card_show) ? field.card_show : [],
      card_land_show: cardLandIds,
      detail_show: showKey === 'detail_show'
        ? detailIds
        : (Array.isArray(field.detail_show) ? field.detail_show : []),
      project_detail_show: showKey === 'project_detail_show'
        ? detailIds
        : (Array.isArray(field.project_detail_show) ? field.project_detail_show : []),
      land_type_show: showKey === 'detail_show'
        ? landTypeIds
        : (Array.isArray(field.land_type_show) ? field.land_type_show : []),
      project_land_type_show: showKey === 'project_detail_show'
        ? landTypeIds
        : (Array.isArray(field.project_land_type_show) ? field.project_land_type_show : []),
    };
  };

  const handleAddLandtype = async () => {
    const name = newLandtypeName.trim();
    if (!name) {
      toast.info('Enter a land type name');
      return;
    }
    try {
      await axios.post(
        `${API_URL}/landtype`,
        { landtype_name: name, calculation: { ...DEFAULT_CALC } },
        { headers: authHeaders() }
      );
      setNewLandtypeName('');
      toast.success('Land type added');
      const ltRes = await axios.get(`${API_URL}/landtype`, { headers: authHeaders() });
      setLandtypes(unwrap(ltRes.data));
    } catch (e) {
      toast.error(e?.response?.data?.error || e.message || 'Failed to add land type');
    }
  };

  const handleDeleteLandtype = async (lt) => {
    if (!window.confirm(`Delete land type “${lt.landtype_name}”?`)) return;
    try {
      await axios.delete(`${API_URL}/landtype/${lt.id}`, { headers: authHeaders() });
      toast.success('Land type deleted');
      setLandtypes((prev) => prev.filter((x) => Number(x.id) !== Number(lt.id)));
      setDirtyLandtypeNames((prev) => {
        const next = { ...prev };
        delete next[lt.id];
        return next;
      });
    } catch (e) {
      toast.error(e?.response?.data?.error || e.message || 'Failed to delete land type');
    }
  };

  const handleSave = async () => {
    const dirtyCount = Object.keys(dirty).length;
    const ltIdsDirty = Object.keys(dirtyLandtypeIds).length;
    const ltNamesDirty = Object.keys(dirtyLandtypeNames).length;

    if (!dirtyCount && !ltIdsDirty && !ltNamesDirty && fromApi) {
      toast.info('No changes to save');
      return;
    }

    setSaving(true);
    try {
      // 1) Land type name updates
      await Promise.all(
        Object.entries(dirtyLandtypeNames).map(([id, name]) =>
          axios.put(
            `${API_URL}/landtype/${id}`,
            { landtype_name: String(name || '').trim() },
            { headers: authHeaders() }
          )
        )
      );

      // 2) Per-field landtype_ids on form inputs
      await Promise.all(
        Object.entries(dirtyLandtypeIds).map(async ([fieldKey, ids]) => {
          const input = findInputByField(formInputs, fieldKey);
          if (!input?.id) return;
          await axios.put(
            `${API_URL}${inputsEndpoint}/${input.id}`,
            { landtype_ids: ids.map(Number), landtype: true },
            { headers: authHeaders() }
          );
        })
      );

      // 3) Property detail field visibility
      const payload = fields
        .filter((f) => !EXCLUDED_LAND_FIELDS.has(f.field))
        .map((field) => buildPayloadForField(field));

      if (!fromApi || dirtyCount || !fields.length) {
        await axios.put(`${API_URL}/property-detail/sync`, payload, {
          headers: authHeaders(),
        });
      } else if (dirtyCount) {
        await axios.put(`${API_URL}/property-detail/sync`, payload, {
          headers: authHeaders(),
        });
      }

      toast.success('Configuration saved');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.error || e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const typeLabel = (t) => t.type_name || t.name || `Type ${resolveTypeId(t) ?? ''}`;
  const catLabel = (c) => c.category_name || c.name || `Category ${resolveCategoryEntityId(c) ?? ''}`;
  const selectedCat = filteredCategories.find(
    (c) => String(resolveCategoryEntityId(c)) === String(categoryId)
  );
  const selectedCatLabel = selectedCat ? catLabel(selectedCat) : 'selected category';
  const hasDirty = Object.keys(dirty).length > 0
    || Object.keys(dirtyLandtypeIds).length > 0
    || Object.keys(dirtyLandtypeNames).length > 0;
  const canSave = !loading && !saving && (hasDirty || !fromApi);

  const renderLandRow = (field) => {
    const detailOn = isDetailEnabled(field);
    const cardOn = isCardLandEnabled(field);
    const landTypeOn = isLandTypeEnabled(field);
    const changed = dirty[field.id] !== undefined
      || (resolveLinkedInput(field)?.field
        && dirtyLandtypeIds[resolveLinkedInput(field).field] !== undefined);
    const name = resolveFieldLabel(field);
    const linked = resolveLinkedInput(field);
    const selectedLtIds = new Set(getEffectiveLandtypeIds(field).map(Number));
    const supportsUnits = field.type === 'area' || linked?.landtype;

    return (
      <li key={field.id ?? field.field ?? name}>
        <div className={`detail-config-row detail-config-row-multi${detailOn ? ' is-on' : ''}${changed ? ' is-dirty' : ''}`}>
          <div className="detail-config-row-main">
            <span className="detail-config-label">{name}</span>
            <span className="detail-config-meta">
              {[field.field, field.type, field.unit].filter(Boolean).join(' · ')}
              {linked?.field && linked.field !== field.field ? ` · form: ${linked.field}` : ''}
            </span>

            {supportsUnits && (
              <div className="landtype-chip-row">
                <span className="landtype-chip-label">Land Types</span>
                <div className="landtype-chips">
                  {landtypes.map((lt) => {
                    const on = selectedLtIds.has(Number(lt.id));
                    return (
                      <button
                        key={lt.id}
                        type="button"
                        className={`landtype-chip${on ? ' is-on' : ''}`}
                        onClick={() => toggleLandtypeForField(field, lt.id)}
                        disabled={!linked}
                        title={!linked ? 'No matching form input with land type' : getLandtypeDisplayName(lt)}
                      >
                        {getLandtypeDisplayName(lt) || `#${lt.id}`}
                      </button>
                    );
                  })}
                </div>
                {!linked && (
                  <span className="landtype-chip-hint">No linked form land-area input for unit selection</span>
                )}
              </div>
            )}
          </div>
          <div className="detail-config-row-actions">
            <button
              type="button"
              className={`detail-config-action-btn${detailOn ? ' is-on' : ''}`}
              onClick={() => toggleDetail(field)}
              aria-pressed={detailOn}
            >
              <ToggleSwitch on={detailOn} label={detailOn ? 'Detail: On' : 'Detail: Off'} />
            </button>
            <button
              type="button"
              className={`detail-config-action-btn${cardOn ? ' is-on' : ''}`}
              onClick={() => toggleCardLand(field)}
              aria-pressed={cardOn}
            >
              <ToggleSwitch on={cardOn} label={cardOn ? 'Card: On' : 'Card: Off'} />
            </button>
            {supportsUnits && (
              <button
                type="button"
                className={`detail-config-action-btn${landTypeOn ? ' is-on' : ''}`}
                onClick={() => toggleLandType(field)}
                aria-pressed={landTypeOn}
              >
                <ToggleSwitch on={landTypeOn} label={landTypeOn ? 'Land Type: On' : 'Land Type: Off'} />
              </button>
            )}
          </div>
        </div>
      </li>
    );
  };

  const renderOtherRow = (field) => {
    const on = isDetailEnabled(field);
    const changed = dirty[field.id] !== undefined;
    const name = resolveFieldLabel(field);
    return (
      <li key={field.id ?? field.field ?? name}>
        <button
          type="button"
          className={`detail-config-row${on ? ' is-on' : ''}${changed ? ' is-dirty' : ''}`}
          onClick={() => toggleDetail(field)}
          aria-pressed={on}
        >
          <div className="detail-config-row-main">
            <span className="detail-config-label">{name}</span>
            <span className="detail-config-meta">
              {[field.field, field.type, field.unit].filter(Boolean).join(' · ')}
            </span>
          </div>
          <ToggleSwitch on={on} />
        </button>
      </li>
    );
  };

  return (
    <div className="animate-fade-in detail-config-page">
      <div className="page-header detail-config-header">
        <h1>{title}</h1>
        <p>{subtitle}</p>
      </div>

      <div className="admin-card detail-config-filters">
        <div className={`detail-config-filter-grid${isProject ? ' is-category-only' : ''}`}>
          {!isProject && (
            <div className="detail-config-field">
              <label htmlFor="detail-config-type">Property Type</label>
              <select
                id="detail-config-type"
                value={typeId}
                onChange={(e) => setTypeId(e.target.value)}
                disabled={loading}
              >
                {types.map((t) => {
                  const id = resolveTypeId(t);
                  return (
                    <option key={String(id)} value={String(id)}>{typeLabel(t)}</option>
                  );
                })}
              </select>
            </div>
          )}
          <div className="detail-config-field">
            <label htmlFor="detail-config-cat">Category</label>
            <select
              id="detail-config-cat"
              value={categoryId}
              onChange={(e) => setCategoryId(e.target.value)}
              disabled={loading || !filteredCategories.length}
            >
              {filteredCategories.map((c) => {
                const id = resolveCategoryEntityId(c);
                return (
                  <option key={String(id)} value={String(id)}>{catLabel(c)}</option>
                );
              })}
            </select>
          </div>
        </div>
        <div className="detail-config-actions">
          <button type="button" className="btn-secondary" onClick={load} disabled={loading || saving}>
            <FiRefreshCw /> Refresh
          </button>
          <button
            type="button"
            className="btn-primary"
            onClick={handleSave}
            disabled={!canSave}
          >
            <FiSave /> {saving ? 'Saving…' : (fromApi ? 'Save Changes' : 'Initialize & Save')}
          </button>
        </div>
      </div>

      {!fromApi && !loading && (
        <div className="admin-card detail-config-banner">
          No saved detail config on the server yet. Click <strong>Initialize &amp; Save</strong> after configuring Land Types and fields.
        </div>
      )}

      {loading ? (
        <div className="admin-card"><p className="detail-config-empty">Loading fields…</p></div>
      ) : !categoryId ? (
        <div className="admin-card"><p className="detail-config-empty">{isProject ? 'No project categories found.' : 'No categories for this property type.'}</p></div>
      ) : (
        <>
          {/* Land Types (global, dynamic) */}
          <div className="admin-card detail-config-fields-card">
            <div className="detail-config-section-head">
              <h2>Land Types</h2>
              <p>
                Loaded from the server. Edit names, add, or remove units. Then assign them to each land area field below.
              </p>
            </div>
            <ul className="landtype-manage-list">
              {landtypes.map((lt) => (
                <li key={lt.id} className="landtype-manage-row">
                  <span className="landtype-manage-id">#{lt.id}</span>
                  <input
                    className="landtype-manage-input"
                    value={getLandtypeDisplayName(lt)}
                    onChange={(e) =>
                      setDirtyLandtypeNames((prev) => ({ ...prev, [lt.id]: e.target.value }))
                    }
                    aria-label={`Land type ${lt.id} name`}
                  />
                  <button
                    type="button"
                    className="landtype-manage-delete"
                    onClick={() => handleDeleteLandtype(lt)}
                    title="Delete land type"
                  >
                    <FiTrash2 />
                  </button>
                </li>
              ))}
            </ul>
            <div className="landtype-add-row">
              <input
                className="landtype-manage-input"
                placeholder="New land type name (e.g. cent)"
                value={newLandtypeName}
                onChange={(e) => setNewLandtypeName(e.target.value)}
              />
              <button type="button" className="btn-secondary" onClick={handleAddLandtype}>
                <FiPlus /> Add Land Type
              </button>
            </div>
          </div>

          {/* Land Area Details Setup */}
          <div className="admin-card detail-config-fields-card">
            <div className="detail-config-section-head">
              <h2>Land Area Details Setup</h2>
              <p>
                Control detail/card visibility for <strong>{selectedCatLabel}</strong>, which Land Types
                each area field may use, and whether the Land Type dropdown appears on the Details page.
              </p>
            </div>
            {landAreaFields.length === 0 ? (
              <p className="detail-config-empty">No land area fields found.</p>
            ) : (
              <ul className="detail-config-list">
                {landAreaFields.map(renderLandRow)}
              </ul>
            )}
          </div>

          {/* Other detail fields */}
          <div className="admin-card detail-config-fields-card">
            <div className="detail-config-section-head">
              <h2>Other Detail Fields</h2>
              <p>
                Enable or disable non-area fields on the detail page for{' '}
                <strong>{selectedCatLabel}</strong>.
              </p>
            </div>
            {otherFields.length === 0 ? (
              <p className="detail-config-empty">No other detail fields found.</p>
            ) : (
              <ul className="detail-config-list">
                {otherFields.map(renderOtherRow)}
              </ul>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default DetailConfigPage;
